// v2 是正式版（GA），spaces.members（含 COHOST）與 config.moderation 都已提供；
// v2beta 只開放給加入 Developer Preview 的專案，不要再用
const MEET_API_BASE = 'https://meet.googleapis.com/v2';

// spaces.* 與 spaces.members.* 都接受 meetings.space.created，這也是唯一要的 scope。
// 它只涵蓋「由本 app 透過 Meet API 建立」的 space：走 Calendar 或手動建的會議設成員時會回
// 403「Permission denied on resource Member (or it might not exist)」，所以會議一律用 spaces.create 建立。

// TODO: 測試階段僅對特定測試帳號設定 cohost，驗證無誤後移除此限制
const COHOST_WHITELIST = [
  'danielwu+testing@bearathome.com.tw',
  'danielwu.ee+pro@gmail.com',
  'co@morph.tw',
  'co+demo@morph.tw',
  'no-reply@eap.morph.tw',
];

export function isCohostWhitelisted(email) {
  return COHOST_WHITELIST.includes(email);
}

// 每次生成連結都固定加入的共同主持人（不受上方白名單限制）
const DEFAULT_COHOST = 'counseling@morph.tw';

// Meet API 以 space 定址，meetingCode 是可用的別名（不分大小寫），
// 直接從 https://meet.google.com/abc-mnop-xyz 這類連結尾端取得，不需要日曆的 event id
export function getMeetingCode(meetingLink) {
  if (!meetingLink) {
    return '';
  }
  const [path] = meetingLink.trim().split(/[?#]/);
  if (!path.includes('meet.google.com/')) {
    return '';
  }
  return path.replace(/\/+$/, '').split('/').pop();
}

// Google API 的錯誤常只有英文 message，依 HTTP 狀態補一句中文判斷，方便使用者／客服回報
function errorHint(err) {
  const reason = err.errors?.[0]?.reason || '';
  if (err.code === 401) {
    return 'Google 授權已過期或無效，請再試一次';
  }
  if (err.code === 403 && (err.status === 'PERMISSION_DENIED' || /insufficient/i.test(`${reason}${err.message}`))) {
    return '權限不足，請確認登入的 Google 帳號，並在授權畫面勾選所有權限';
  }
  if (err.code === 429 || reason === 'rateLimitExceeded' || err.status === 'RESOURCE_EXHAUSTED') {
    return 'Google 請求過於頻繁，請稍候再試';
  }
  if (err.code >= 500) {
    return 'Google 服務暫時異常，請稍候再試';
  }
  return '';
}

function errorDetail(e) {
  const err = e?.result?.error || e?.error;
  if (err && typeof err === 'object') {
    const hint = errorHint(err);
    const status = err.status || err.code;
    return `${hint ? `${hint}。` : ''}${err.message || '未知錯誤'}${status ? `（${status}）` : ''}`;
  }
  return e?.message || '未知錯誤';
}

function stepError(step, detail, cause) {
  const err = new Error(`${step}：${detail}`);
  err.cause = cause;
  return err;
}

// 三站共用 management 的 OAuth client
const GOOGLE_CLIENT_ID = '288050987194-puujt09mjushdufdau92fvmp9osaghce.apps.googleusercontent.com';
const MEET_CREATE_SCOPE = 'https://www.googleapis.com/auth/meetings.space.created';

const TOKEN_ERRORS = {
  access_denied: '未同意 Google 授權',
  invalid_scope: '授權範圍設定錯誤，請聯絡系統管理員',
  popup_closed: '授權視窗已關閉，尚未完成授權',
  popup_failed_to_open: '授權視窗被瀏覽器封鎖，請允許本網站開啟彈出視窗後再試',
};

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const GIS_LOAD_ERROR = '無法載入 Google 登入元件（accounts.google.com），請確認網路連線，或暫停阻擋廣告／追蹤的擴充套件後重新整理頁面';

// Google 登入元件（GIS）由 management / professional 的 html 以 async script 預先載入，
// 但不依賴它的 onload 順序（async script 從快取載入時可能比 inline script 先執行，
// 導致 onload 找不到函式、token client 沒建立），這裡自己等它載入並建立 token client；
// html 沒載入成功時再補插一次 script。
let gisPromise;
function loadGis() {
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve(window.google.accounts.oauth2);
  }
  if (!gisPromise) {
    gisPromise = new Promise((resolve, reject) => {
      let script = document.querySelector(`script[src="${GIS_SRC}"]`);
      if (!script) {
        script = document.createElement('script');
        script.src = GIS_SRC;
        script.async = true;
        document.head.appendChild(script);
      }
      const fail = (cause) => {
        script.remove();
        reject(stepError('Google 元件載入失敗', GIS_LOAD_ERROR, cause));
      };
      const timer = setTimeout(() => fail(new Error('timeout')), 10000);
      script.addEventListener('load', () => {
        clearTimeout(timer);
        if (window.google?.accounts?.oauth2) {
          resolve(window.google.accounts.oauth2);
        } else {
          fail(new Error('google.accounts.oauth2 missing'));
        }
      });
      script.addEventListener('error', (e) => {
        clearTimeout(timer);
        fail(e);
      });
    });
    // 失敗後允許下次重試（fail 已移除舊的 script，會重新插入）
    gisPromise.catch(() => {
      gisPromise = undefined;
    });
  }
  return gisPromise;
}

let tokenClient;
let accessToken;
let accessTokenExpiresAt = 0;
// token client 的 callback 是共用的，同一時間只能有一個授權請求在等結果。
let pendingAuth;

function hasMeetScope(oauth2, resp) {
  return oauth2.hasGrantedAllScopes(resp, MEET_CREATE_SCOPE);
}

async function requestAccessToken() {
  const oauth2 = await loadGis();
  if (!tokenClient) {
    tokenClient = oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: MEET_CREATE_SCOPE,
      callback: () => {},
    });
  }
  if (pendingAuth) {
    pendingAuth.reject(stepError('Google 授權失敗', '已重新發起授權，此次請求取消'));
  }
  const client = tokenClient;
  return new Promise((resolve, reject) => {
    // 已結束（成功、失敗或被新請求取代）的請求直接忽略之後才到的回呼。
    // 例如授權視窗先回報 popup_closed、之後 token 才到，不能再拿去建第二個會議
    const req = {};
    const settle = (fn) => (value) => {
      if (pendingAuth !== req) {
        return;
      }
      pendingAuth = undefined;
      fn(value);
    };
    req.resolve = settle(resolve);
    req.reject = settle(reject);
    pendingAuth = req;

    client.callback = (resp) => {
      if (resp.error !== undefined) {
        const detail = TOKEN_ERRORS[resp.error] || resp.error_description || resp.error;
        req.reject(stepError('Google 授權失敗', detail, resp));
        return;
      }
      if (!hasMeetScope(oauth2, resp)) {
        req.reject(stepError('Google 授權不足', '未勾選 Google Meet 會議權限，請重新生成並在授權畫面勾選', resp));
        return;
      }
      accessToken = resp.access_token;
      // 提早一分鐘視為過期，避免送出途中失效
      accessTokenExpiresAt = Date.now() + (Number(resp.expires_in) - 60) * 1000;
      req.resolve(accessToken);
    };
    client.error_callback = (err) => {
      req.reject(stepError('Google 授權失敗', TOKEN_ERRORS[err?.type] || err?.message || err?.type || '未知錯誤', err));
    };
    try {
      client.requestAccessToken({ prompt: accessTokenExpiresAt === 0 ? 'consent' : '' });
    } catch (e) {
      req.reject(stepError('Google 授權失敗', e?.message || '無法開啟授權視窗', e));
    }
  });
}

// token 還有效就直接用，不再跳授權視窗
async function ensureAccessToken() {
  if (accessToken && Date.now() < accessTokenExpiresAt) {
    return accessToken;
  }
  return requestAccessToken();
}

// 把 Meet API 的失敗包成帶步驟名稱的 Error，呼叫端顯示時才知道卡在哪一步。
// 直接用 fetch 帶 access token 呼叫，不依賴 gapi client
async function meetRequest(step, {
  path, method, params, body,
}) {
  const token = await ensureAccessToken();
  const url = new URL(path);
  Object.entries(params || {}).forEach(([k, v]) => url.searchParams.set(k, v));
  let rsp;
  try {
    rsp = await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw stepError(step, `無法連線到 Google（${e.message}），請確認網路後再試`, e);
  }
  const data = await rsp.json().catch(() => ({}));
  if (!rsp.ok) {
    if (rsp.status === 401) {
      accessToken = undefined;
    }
    const e = { result: { error: data.error || { code: rsp.status, message: rsp.statusText } } };
    throw stepError(step, errorDetail(e), e);
  }
  return data;
}

// 用 Meet API 建立會議空間，回傳 { name, meetingCode, meetingUri, config }
async function createMeetSpace() {
  return meetRequest('建立 Google Meet 會議失敗', {
    path: `${MEET_API_BASE}/spaces`,
    method: 'POST',
    body: {},
  });
}

// 只有 spaces.get 明確接受 meetingCode 別名，members.create 的 parent 只寫 spaces/{space}，
// 所以先用 meeting code 換回正式的 resource name 再往下打。
async function getSpace(meetingLink) {
  const code = getMeetingCode(meetingLink);
  if (!code) {
    throw new Error(`無法從連結取得會議代碼：${meetingLink || '(空)'}`);
  }
  return meetRequest('取得會議空間', {
    path: `${MEET_API_BASE}/spaces/${code}`,
    method: 'GET',
  });
}

// 共同主持人是「主持人管理」（moderation）開啟後才有的功能，
// 新建 space 的預設值依組織政策、通常是關閉，所以設 cohost 前先開。
async function enableModeration(space) {
  if (space.config?.moderation === 'ON') {
    return space;
  }
  return meetRequest('開啟主持人管理', {
    path: `${MEET_API_BASE}/${space.name}`,
    method: 'PATCH',
    params: { updateMask: 'config.moderation' },
    body: { config: { moderation: 'ON' } },
  });
}

// 存預約前先取得授權：授權視窗必須在使用者點擊後立刻開啟，
// 等存檔 API 回來才開會被瀏覽器當成彈出廣告封鎖。token 還有效時不會跳視窗
export function prepareMeetAuth() {
  return ensureAccessToken();
}

async function addSpaceCohost(space, email) {
  await enableModeration(space);
  try {
    await meetRequest('新增共同主持人', {
      path: `${MEET_API_BASE}/${space.name}/members`,
      method: 'POST',
      body: { email, role: 'COHOST' },
    });
  } catch (e) {
    const status = e.cause?.result?.error?.status;
    // 已經是成員（例如專業人士本人就是預設共同主持人）就當作成功，不重複加入
    if (status === 'ALREADY_EXISTS') {
      return;
    }
    if (status === 'PERMISSION_DENIED') {
      e.message += '。成員設定只對由本系統生成的會議有效，手動貼上或舊版生成的連結請重新按「生成連結」';
    }
    throw e;
  }
}

export async function addCohost(meetingLink, email) {
  const space = await getSpace(meetingLink);
  await addSpaceCohost(space, email);
}

// 生成連結：授權 → 用 Meet API 建會議空間（之後才能對它設定共同主持人）→ 加入固定的共同主持人。
// 建立會議失敗時丟出的 Error.message 帶有卡住的步驟與原因，可直接顯示給使用者；
// 加入共同主持人失敗不影響連結，回傳在 cohostError 讓呼叫端提示。
export async function generateMeetLink() {
  const space = await createMeetSpace();
  if (!space?.meetingUri) {
    throw stepError('建立 Google Meet 會議失敗', 'Google 未回傳會議連結');
  }
  let cohostError;
  try {
    await addSpaceCohost(space, DEFAULT_COHOST);
  } catch (e) {
    cohostError = new Error(`無法將 ${DEFAULT_COHOST} 設為共同主持人。${e.message}`);
    cohostError.cause = e;
  }
  return { meetingUri: space.meetingUri, cohostError };
}
